import 'css/fonts.css';

import { Auth0Provider } from '@auth0/nextjs-auth0/client';
import NextApp, { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import { PropsWithChildren } from 'react';

import { CalmTheme, DesignSystemProvider, SleepTheme } from '@calm-web/design-system';

import AnalyticsContextProvider from '@/components/app/layout/AnalyticsContextProvider';
import AppLayout from '@/components/app/layout/AppLayout';
import { AppHeaderProps } from '@/components/app/layout/AppLayout/types';
import AppProviders from '@/components/app/layout/AppProviders';
import IntlProvider from '@/components/layout/IntlProvider';
import PageWrapper from '@/components/layout/PageWrapper';
import StoreProvider from '@/components/layout/StoreProvider';
import { type AuthPrompt } from '@/context/auth/AuthPromptProvider';
import AuthProvider from '@/context/auth/AuthProvider';
import { getProfileRoute } from '@/hooks/auth/authRoutes';
import useIsCalmSleepApp from '@/hooks/useIsCalmSleepApp';
import { usePinnedCspNonce } from '@/hooks/usePinnedCspNonce';
import { AppInitialState } from '@/store';
import { getBundledMessages } from '@/utils/locale/pagesRouterMessages';

type MyAppLayoutProps = PropsWithChildren;

// this should be equivalent to (classic)/layout and (sleep)/layout for pages router
const MyAppLayout = ({ children }: MyAppLayoutProps) => {
	const isCalmSleepApp = useIsCalmSleepApp();
	const theme = isCalmSleepApp ? SleepTheme : CalmTheme;
	return (
		<Auth0Provider profileRoute={getProfileRoute(isCalmSleepApp)}>
			<DesignSystemProvider theme={theme}>{children}</DesignSystemProvider>
		</Auth0Provider>
	);
};

const MyApp = ({
	Component,
	pageProps,
}: AppProps<{
	initialState: AppInitialState;
	appHeaderProps: AppHeaderProps;
	hideAcceptCookiesModal?: boolean;
	defaultAuthPrompt?: AuthPrompt;
	nonce?: string | null;
}>) => {
	// this should be equivalent to ClassicPageServerProvider and SleepPageServerProvider
	const isCalmSleepApp = useIsCalmSleepApp();
	const amplitudeExperimentsDeploymentKey = isCalmSleepApp
		? process.env.NEXT_PUBLIC_SLEEP_AMPLITUDE_EXPERIMENTS_DEPLOYMENT_KEY
		: process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_DEPLOYMENT_KEY;
	const amplitudeToken = isCalmSleepApp
		? process.env.NEXT_PUBLIC_SLEEP_AMPLITUDE_TOKEN
		: process.env.NEXT_PUBLIC_AMPLITUDE_TOKEN;

	const locale = pageProps.initialState?.locale || 'en';
	const messages = getBundledMessages(locale);

	// `MyApp.getInitialProps` re-runs on every client-side navigation (not just
	// the initial SSR), and `ctx.req` — and so the nonce — is only present on
	// that original request; every navigation after that supplies `null` (see
	// its doc comment below). The browser only ever enforces the CSP header from
	// the document's ORIGINAL response, so an inline <Script> that mounts for
	// the first time on a LATER navigation (e.g. `gtm-init` appearing once the
	// user leaves a `/password-reset/*` page) must still carry that SAME nonce
	// or it won't match the header actually in force. `MyApp` itself never
	// remounts between pages, which is what makes pinning here work — see
	// usePinnedCspNonce's doc comment.
	const nonce = usePinnedCspNonce(pageProps.nonce);

	return (
		<>
			<Head>
				<link rel="shortcut icon" type="image/x-icon" href="/favicon.ico" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
			</Head>
			<IntlProvider locale={locale} messages={messages}>
				<MyAppLayout>
					<StoreProvider initialState={pageProps.initialState}>
						<AnalyticsContextProvider
							amplitudeExperimentsDeploymentKey={amplitudeExperimentsDeploymentKey}
							amplitudeToken={amplitudeToken}
						>
							<PageWrapper
								amplitudeToken={amplitudeToken}
								defaultAuthPrompt={pageProps.defaultAuthPrompt}
								hideAcceptCookiesModal={pageProps.hideAcceptCookiesModal}
								nonce={nonce}
							>
								<AuthProvider mode={isCalmSleepApp ? 'sleep' : 'classic'}>
									<AppProviders>
										<AppLayout appHeaderProps={pageProps.appHeaderProps}>
											{/* Override pageProps.nonce (the raw, per-render value — `null` on every
											    client-side navigation, see MyApp.getInitialProps below) with the
											    pinned one, so a page component that reads its own `nonce` prop
											    (e.g. amazonprimestudent/index.tsx's raw inline <script>) gets the
											    same document-lifetime value LayoutScripts/PageWrapper already do. */}
											<Component {...pageProps} nonce={nonce} />
										</AppLayout>
									</AppProviders>
								</AuthProvider>
							</PageWrapper>
						</AnalyticsContextProvider>
					</StoreProvider>
				</MyAppLayout>
			</IntlProvider>
		</>
	);
};

// The single choke point for the CSP nonce on the Pages Router: `getServerSideProps`
// is per-page opt-in (dozens of pages call `getInitialServerState` directly and
// return their own `{ props: { initialState } }` with no nonce key, and a handful
// of pages define neither `getServerSideProps` nor `getStaticProps` at all), so
// wiring the nonce through any one of those would leave the rest serving
// LayoutScripts' inline scripts with no nonce — a fresh CSP violation on every page
// this doesn't cover. `getInitialProps` on the custom App runs, with a real
// `ctx.req`, on every request to every page that renders dynamically — which is
// every page in this app EXCEPT the few that still define their own
// `getStaticProps`/ISR (those pre-render once and reuse that HTML across many
// requests, so baking in one request's nonce would itself go stale the moment a
// later request's response header advertises a different one — those pages must
// stay off static generation for the nonce to ever be correct; see the comment on
// each of their `getServerSideProps` for why they were converted). A page's own
// `getServerSideProps` props are merged over this by Next itself afterward, so a
// page that already threads its own `nonce` key (there are none today) would win;
// this is just the floor every dynamically-rendered page gets for free.
MyApp.getInitialProps = async (context: AppContext) => {
	const appProps = await NextApp.getInitialProps(context);
	// Only present on the initial SSR request — `ctx.req` is undefined on every
	// client-side navigation after that, so this resolves to `null` from the
	// second render onward. That's fine here: `MyApp` itself pins whatever
	// non-null value it first receives (via `usePinnedCspNonce` above) rather
	// than trusting this value on every render.
	const nonceHeader = context.ctx.req?.headers['x-nonce'];

	return {
		...appProps,
		pageProps: {
			...appProps.pageProps,
			nonce: typeof nonceHeader === 'string' ? nonceHeader : null,
		},
	};
};

export default MyApp;
